import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import api, { getCurrentUser } from '../services/api';
import './ReportViewer.css';

function ReportViewer() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [authError, setAuthError] = useState('');
  const [user, setUser] = useState(null);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getCurrentUser();
        setUser(response.data);
        console.log('User authenticated:', response.data);
        // Load operators after authentication
        loadOperators();
      } catch (err) {
        console.error('Auth check failed:', err);
        setAuthError('Not authenticated. Redirecting to login...');
        setTimeout(() => navigate('/staff/login'), 2000);
      }
    };
    checkAuth();
  }, [navigate]);

  const loadOperators = async () => {
    try {
      setOperatorsLoading(true);
      const response = await api.get('/reports/operators/');
      setOperators(response.data.operators || []);
    } catch (err) {
      console.error('Failed to load operators:', err);
      toast.error('Failed to load operators list');
    } finally {
      setOperatorsLoading(false);
    }
  };

  // Helper function to generate hourly activity data from tickets
  const generateHourlyActivityData = (tickets) => {
    const hourlyData = {};
    
    // Initialize all hours
    for (let i = 0; i < 24; i++) {
      hourlyData[i] = 0;
    }
    
    // Count tickets by hour
    tickets.forEach(ticket => {
      if (ticket.created_at) {
        const hour = new Date(ticket.created_at).getHours();
        hourlyData[hour] = (hourlyData[hour] || 0) + 1;
      }
    });
    
    // Convert to array format for chart
    return Object.keys(hourlyData).map(hour => ({
      hour: `${hour}:00`,
      tickets: hourlyData[hour]
    }));
  };

  const handleLogout = () => {
    api.post('/auth/logout/')
      .then(() => {
        toast.success('Logged out successfully');
        navigate('/staff/login');
      })
      .catch(() => {
        navigate('/staff/login');
      });
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setError('');
    setReport(null);

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
      };
      
      // Add operator_id if selected
      if (selectedOperator) {
        payload.operator_id = parseInt(selectedOperator);
      }
      
      const response = await api.post('/reports/generate/', payload);
      setReport(response.data);
      toast.success('Report generated successfully');
    } catch (err) {
      console.error('Report generation error:', err);
      let errorMsg = 'Failed to generate report';
      
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (!report) return;

    // Create a simple PDF-like export (using HTML to PDF conversion)
    const content = generateReportHTML();
    const element = document.createElement('div');
    element.innerHTML = content;
    
    // For production, consider using a library like jsPDF or html2pdf
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const generateReportHTML = () => {
    if (!report) return '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Queue Management Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #667eea; padding-bottom: 10px; }
          h2 { color: #667eea; margin-top: 20px; }
          .section { margin: 20px 0; padding: 15px; background: #f5f5f5; border-radius: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #667eea; color: white; }
          .stat { display: inline-block; margin: 10px 20px 10px 0; }
          .stat-label { font-weight: bold; color: #667eea; }
          .stat-value { font-size: 24px; color: #333; }
        </style>
      </head>
      <body>
        <h1>Queue Management System - Report</h1>
        <p><strong>Period:</strong> ${report.period.start_date} to ${report.period.end_date}</p>
        
        <h2>Summary Statistics</h2>
        <div class="section">
          <div class="stat">
            <div class="stat-label">Total Tickets</div>
            <div class="stat-value">${report.summary.total_tickets}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Served</div>
            <div class="stat-value">${report.summary.total_served}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Cancelled</div>
            <div class="stat-value">${report.summary.total_cancelled}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Waiting</div>
            <div class="stat-value">${report.summary.total_waiting}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Avg Wait Time</div>
            <div class="stat-value">${report.summary.average_wait_time_minutes} min</div>
          </div>
        </div>

        <h2>Most Chosen Faculty</h2>
        <div class="section">
          ${report.most_chosen_faculty ? `
            <table>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Ticket Count</th>
              </tr>
              <tr>
                <td>${report.most_chosen_faculty.name}</td>
                <td>${report.most_chosen_faculty.code}</td>
                <td>${report.most_chosen_faculty.ticket_count}</td>
              </tr>
            </table>
          ` : '<p>No data available</p>'}
        </div>

        <h2>Most Chosen Section</h2>
        <div class="section">
          ${report.most_chosen_section ? `
            <table>
              <tr>
                <th>Name</th>
                <th>Faculty</th>
                <th>Ticket Count</th>
              </tr>
              <tr>
                <td>${report.most_chosen_section.name}</td>
                <td>${report.most_chosen_section.faculty}</td>
                <td>${report.most_chosen_section.ticket_count}</td>
              </tr>
            </table>
          ` : '<p>No data available</p>'}
        </div>

        <h2>Most Chosen Service</h2>
        <div class="section">
          ${report.most_chosen_service ? `
            <table>
              <tr>
                <th>Name</th>
                <th>Section</th>
                <th>Ticket Count</th>
              </tr>
              <tr>
                <td>${report.most_chosen_service.name}</td>
                <td>${report.most_chosen_service.section}</td>
                <td>${report.most_chosen_service.ticket_count}</td>
              </tr>
            </table>
          ` : '<p>No data available</p>'}
        </div>

        <h2>Most Active Operator</h2>
        <div class="section">
          ${report.most_active_operator ? `
            <table>
              <tr>
                <th>Name</th>
                <th>Employee ID</th>
                <th>Counter</th>
                <th>Tickets Called</th>
                <th>Tickets Completed</th>
              </tr>
              <tr>
                <td>${report.most_active_operator.name}</td>
                <td>${report.most_active_operator.employee_id}</td>
                <td>${report.most_active_operator.counter_number}</td>
                <td>${report.most_active_operator.tickets_called}</td>
                <td>${report.most_active_operator.tickets_completed}</td>
              </tr>
            </table>
          ` : '<p>No data available</p>'}
        </div>
      </body>
      </html>
    `;
  };

  // Show loading or auth error
  if (authError) {
    return (
      <div className="report-viewer-container">
        <div className="report-header">
          <h1>📊 Navbatlar bo'yicha hisobot</h1>
        </div>
        <div className="report-content">
          <div className="error-message">{authError}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="report-viewer-container">
        <div className="report-header">
          <h1>📊 Navbatlar bo'yicha hisobot</h1>
        </div>
        <div className="report-content">
          <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-viewer-container">
      <div className="report-header">
        <h1>📊 Navbatlar bo'yicha hisobot</h1>
        <button className="logout-btn" onClick={handleLogout}>Chiqish</button>
      </div>

      <div className="report-content">
        <div className="report-form-section">
          <h2>Hisobotni yaratish</h2>
          <form onSubmit={handleGenerateReport} className="report-form">
            <div className="form-group">
              <label htmlFor="startDate">Boshlanish sanasi:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">Tugash sanasi:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="operator">Operator tanlang (ixtiyoriy):</label>
              <select
                id="operator"
                value={selectedOperator}
                onChange={(e) => setSelectedOperator(e.target.value)}
                disabled={operatorsLoading}
              >
                <option value="">Barchasi</option>
                {operators.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.name} ({op.counter_number}) - {op.faculty}
                  </option>
                ))}
              </select>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="generate-btn"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </form>
        </div>

        {report && (
          <div className="report-results">
            <div className="results-header">
              <h2>Hisobot natijalar</h2>
              <button className="export-btn" onClick={handleExportPDF}>
                📥 PDF shaklida yuklab olish
              </button>
            </div>

            <div className="period-info">
              <p><strong>Oraliq:</strong> {report.period.start_date} dan {report.period.end_date} gacha</p>
            </div>

            {/* Summary Statistics */}
            <div className="report-section">
              <h3>📈 Yakuniy statistikalar</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Umumiy navbatlar</div>
                  <div className="stat-value">{report.summary.total_tickets}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Xizmat ko'rsatilgan</div>
                  <div className="stat-value">{report.summary.total_served}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Rad etilgan</div>
                  <div className="stat-value">{report.summary.total_cancelled}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Kutilayotganlar</div>
                  <div className="stat-value">{report.summary.total_waiting}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">O'rtacha kutish vaqti</div>
                  <div className="stat-value">{report.summary.average_wait_time_minutes} min</div>
                </div>
              </div>

              {/* Summary Chart */}
              <div className="chart-container">
                <h4>Navbatlar holati</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Xizmat ko\'rsatilgan', value: report.summary.total_served, fill: '#27ae60' },
                        { name: 'Kutilayotgan', value: report.summary.total_waiting, fill: '#f39c12' },
                        { name: 'Rad etilgan', value: report.summary.total_cancelled, fill: '#e74c3c' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#27ae60" />
                      <Cell fill="#f39c12" />
                      <Cell fill="#e74c3c" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Hourly Activity Chart */}
              {report.hourly_activity && report.hourly_activity.some(d => d.tickets > 0) && (
                <div className="chart-container">
                  <h4>⏰ Soat bo'yicha navbatlar faoliyati</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={report.hourly_activity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="tickets"
                        stroke="#667eea"
                        strokeWidth={2}
                        dot={{ fill: '#667eea', r: 4 }}
                        activeDot={{ r: 6 }}
                        name="Navbatlar soni"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Most Chosen Faculty */}
            <div className="report-section">
              <h3>🏢 Eng ko'p tanlangan fakultet</h3>
              {report.most_chosen_faculty ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Kod</th>
                      <th>Navbatlar soni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{report.most_chosen_faculty.name}</td>
                      <td>{report.most_chosen_faculty.code}</td>
                      <td>{report.most_chosen_faculty.ticket_count}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Ma'lumot mavjud emas!</p>
              )}
            </div>

            {/* Most Chosen Section */}
            <div className="report-section">
              <h3>📋 Eng ko'p tanlangan bo'lim</h3>
              {report.most_chosen_section ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Fakultet</th>
                      <th>Navbatlar soni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{report.most_chosen_section.name}</td>
                      <td>{report.most_chosen_section.faculty}</td>
                      <td>{report.most_chosen_section.ticket_count}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Ma'lumot mavjud emas</p>
              )}
            </div>

            {/* Most Chosen Service */}
            <div className="report-section">
              <h3>🔧 Eng ko'p tanlangan xizmat</h3>
              {report.most_chosen_service ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Bo'lim</th>
                      <th>Navbatlar soni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{report.most_chosen_service.name}</td>
                      <td>{report.most_chosen_service.section}</td>
                      <td>{report.most_chosen_service.ticket_count}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Ma'lumot mavjud emas</p>
              )}
            </div>

            {/* Most Active Operator */}
            <div className="report-section">
              <h3>👤 Eng faol operator</h3>
              {report.most_active_operator ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Xodim ID</th>
                      <th>Nechta</th>
                      <th>Chaqirilgan navbatlar</th>
                      <th>Tugallangan navbatlar</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{report.most_active_operator.name}</td>
                      <td>{report.most_active_operator.employee_id}</td>
                      <td>{report.most_active_operator.counter_number}</td>
                      <td>{report.most_active_operator.tickets_called}</td>
                      <td>{report.most_active_operator.tickets_completed}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Ma'lumot mavjud emas</p>
              )}
            </div>

            {/* Selected Operator Details */}
            {report.operator_details && (
              <div className="report-section">
                <h3>👤 Tanlangan operator - Batafsil</h3>
                
                {/* Operator Info Cards */}
                <div className="operator-info-cards">
                  <div className="info-card">
                    <div className="info-label">Operator nomi</div>
                    <div className="info-value">{report.operator_details.name}</div>
                  </div>
                  <div className="info-card">
                    <div className="info-label">Xodim ID</div>
                    <div className="info-value">{report.operator_details.employee_id}</div>
                  </div>
                  <div className="info-card">
                    <div className="info-label">Counter</div>
                    <div className="info-value">{report.operator_details.counter_number}</div>
                  </div>
                  <div className="info-card">
                    <div className="info-label">Fakultet</div>
                    <div className="info-value">{report.operator_details.faculty}</div>
                  </div>
                </div>

                {/* Operator Statistics Chart */}
                <div className="chart-box" style={{ marginTop: '20px' }}>
                  <h4>Operator faoliyati</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        {
                          name: report.operator_details.name,
                          'Chaqirilgan': report.operator_details.tickets_called,
                          'Tugallangan': report.operator_details.tickets_completed,
                          'Rad etilgan': report.operator_details.tickets_cancelled,
                          'Kutilayotgan': report.operator_details.tickets_waiting
                        }
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Chaqirilgan" fill="#3498db" />
                      <Bar dataKey="Tugallangan" fill="#27ae60" />
                      <Bar dataKey="Rad etilgan" fill="#e74c3c" />
                      <Bar dataKey="Kutilayotgan" fill="#f39c12" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Operator Metrics */}
                <div style={{ marginTop: '20px' }}>
                  <h4>Metrikalar</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <span className="metric-badge success">
                      Tugallangan: {report.operator_details.tickets_completed}
                    </span>
                    <span className="metric-badge">
                      Chaqirilgan: {report.operator_details.tickets_called}
                    </span>
                    <span className="metric-badge warning">
                      Kutilayotgan: {report.operator_details.tickets_waiting}
                    </span>
                    <span className="metric-badge danger">
                      Rad etilgan: {report.operator_details.tickets_cancelled}
                    </span>
                    <span className="metric-badge" style={{ background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }}>
                      O'rtacha vaqt: {report.operator_details.average_wait_time_minutes} min
                    </span>
                  </div>
                </div>

                {/* Operator Hourly Activity Chart */}
                {report.operator_hourly_activity && report.operator_hourly_activity.some(d => d.tickets > 0) && (
                  <div className="chart-box" style={{ marginTop: '20px' }}>
                    <h4>⏰ Operator soat bo'yicha faoliyati</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={report.operator_hourly_activity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="tickets" fill="#667eea" name="Navbatlar soni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportViewer;
